import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import { ownersApiService } from "@services/apis/Owners";
import {
  isOwnersApiFailure,
  Owner,
  OwnerApiKey,
  OwnerIpWhitelistEntry,
  toOwnersBoolean,
} from "@services/models/Owners/OwnersModels";
import { formatDisplayDate } from "@utils/safeDate";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import AddOwnerIpWhitelistSlider from "./components/AddOwnerIpWhitelistSlider";
import CreateOwnerSlider from "./components/CreateOwnerSlider";
import UpdateOwnerContactSlider from "./components/UpdateOwnerContactSlider";

const yesNoFormatter = (value: unknown) => (toOwnersBoolean(value as any) ? "Yes" : "No");

const OwnersManagement = () => {
  const ownersGridRef = useRef<any>(null);
  const apiKeysGridRef = useRef<any>(null);
  const whitelistGridRef = useRef<any>(null);

  const { handleError, handleSuccess } = useSnackbarMessage();

  const [owners, setOwners] = useState<Owner[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState("");
  const [ownersSearch, setOwnersSearch] = useState("");

  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);

  const [apiKeys, setApiKeys] = useState<OwnerApiKey[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeysError, setApiKeysError] = useState("");
  const [revokingKeyId, setRevokingKeyId] = useState<number | null>(null);

  const [whitelistEntries, setWhitelistEntries] = useState<OwnerIpWhitelistEntry[]>(
    [],
  );
  const [whitelistLoading, setWhitelistLoading] = useState(false);
  const [whitelistError, setWhitelistError] = useState("");
  const [togglingWhitelistId, setTogglingWhitelistId] = useState<number | null>(
    null,
  );
  const [deletingWhitelistId, setDeletingWhitelistId] = useState<number | null>(
    null,
  );

  const [createOwnerOpen, setCreateOwnerOpen] = useState(false);
  const [updateContactOpen, setUpdateContactOpen] = useState(false);
  const [addWhitelistOpen, setAddWhitelistOpen] = useState(false);

  const fetchOwners = useCallback(async () => {
    try {
      setOwnersLoading(true);
      setOwnersError("");
      const res = await ownersApiService.listOwners();
      if (isOwnersApiFailure(res)) {
        setOwners([]);
        setOwnersError(res.msg || "Failed to fetch owners");
        return;
      }
      setOwners(res.data?.owners ?? []);
    } catch (err: any) {
      setOwners([]);
      setOwnersError(err?.response?.data?.msg || "Failed to fetch owners");
    } finally {
      setOwnersLoading(false);
    }
  }, []);

  const fetchApiKeys = useCallback(async (ownerId: number) => {
    try {
      setApiKeysLoading(true);
      setApiKeysError("");
      const res = await ownersApiService.listOwnerApiKeys(ownerId);
      if (isOwnersApiFailure(res)) {
        setApiKeys([]);
        setApiKeysError(res.msg || "Failed to fetch API keys");
        return;
      }
      setApiKeys(res.data?.apiKeys ?? []);
    } catch (err: any) {
      setApiKeys([]);
      setApiKeysError(err?.response?.data?.msg || "Failed to fetch API keys");
    } finally {
      setApiKeysLoading(false);
    }
  }, []);

  const fetchWhitelist = useCallback(async (ownerId: number) => {
    try {
      setWhitelistLoading(true);
      setWhitelistError("");
      const res = await ownersApiService.listOwnerIpWhitelist(ownerId);
      if (isOwnersApiFailure(res)) {
        setWhitelistEntries([]);
        setWhitelistError(res.msg || "Failed to fetch IP whitelist");
        return;
      }
      setWhitelistEntries(res.data?.entries ?? []);
    } catch (err: any) {
      setWhitelistEntries([]);
      setWhitelistError(
        err?.response?.data?.msg || "Failed to fetch IP whitelist",
      );
    } finally {
      setWhitelistLoading(false);
    }
  }, []);

  const refreshSelectedOwnerData = useCallback(async () => {
    if (!selectedOwner?.ownerId) return;
    await Promise.all([
      fetchApiKeys(selectedOwner.ownerId),
      fetchWhitelist(selectedOwner.ownerId),
    ]);
  }, [selectedOwner?.ownerId, fetchApiKeys, fetchWhitelist]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  useEffect(() => {
    if (!selectedOwner?.ownerId) {
      setApiKeys([]);
      setWhitelistEntries([]);
      setApiKeysError("");
      setWhitelistError("");
      return;
    }
    refreshSelectedOwnerData();
  }, [selectedOwner?.ownerId, refreshSelectedOwnerData]);

  const ownersFiltered = useMemo(() => {
    if (!ownersSearch) return owners;
    const term = ownersSearch.toLowerCase();
    return owners.filter((o) => {
      return (
        o.ownerId.toString().includes(term) ||
        o.code.toLowerCase().includes(term) ||
        o.displayName.toLowerCase().includes(term) ||
        (o.email?.toLowerCase().includes(term) ?? false) ||
        (o.phone?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [owners, ownersSearch]);

  const handleRevokeKey = useCallback(
    async (ownerApiKeyId: number) => {
      setRevokingKeyId(ownerApiKeyId);
      try {
        const res = await ownersApiService.revokeOwnerApiKey({ ownerApiKeyId });
        if (isOwnersApiFailure(res)) {
          handleError({ message: res.msg || "Failed to revoke API key" });
          return;
        }
        handleSuccess({ message: res.msg || "API key revoked" });
        await refreshSelectedOwnerData();
      } catch (err: any) {
        handleError({
          message: err?.response?.data?.msg || "Failed to revoke API key",
        });
      } finally {
        setRevokingKeyId(null);
      }
    },
    [handleError, handleSuccess, refreshSelectedOwnerData],
  );

  const handleToggleWhitelist = useCallback(
    async (id: number, active: boolean) => {
      setTogglingWhitelistId(id);
      try {
        const res = await ownersApiService.toggleOwnerIpWhitelistEntry({
          id,
          active,
        });
        if (isOwnersApiFailure(res)) {
          handleError({ message: res.msg || "Failed to toggle whitelist entry" });
          return;
        }
        handleSuccess({
          message: res.msg || (active ? "IP enabled" : "IP disabled"),
        });
        await refreshSelectedOwnerData();
      } catch (err: any) {
        handleError({
          message:
            err?.response?.data?.msg || "Failed to toggle whitelist entry",
        });
      } finally {
        setTogglingWhitelistId(null);
      }
    },
    [handleError, handleSuccess, refreshSelectedOwnerData],
  );

  const handleDeleteWhitelist = useCallback(
    async (id: number) => {
      setDeletingWhitelistId(id);
      try {
        const res = await ownersApiService.deleteOwnerIpWhitelistEntry({ id });
        if (isOwnersApiFailure(res)) {
          handleError({ message: res.msg || "Failed to delete whitelist entry" });
          return;
        }
        handleSuccess({ message: res.msg || "Entry deleted" });
        await refreshSelectedOwnerData();
      } catch (err: any) {
        handleError({
          message:
            err?.response?.data?.msg || "Failed to delete whitelist entry",
        });
      } finally {
        setDeletingWhitelistId(null);
      }
    },
    [handleError, handleSuccess, refreshSelectedOwnerData],
  );

  const ownersColumnDefs: ColDef[] = useMemo(
    () => [
      { headerName: "Owner ID", field: "ownerId", minWidth: 90, maxWidth: 110 },
      { headerName: "Code", field: "code", minWidth: 110 },
      { headerName: "Display Name", field: "displayName", flex: 1, minWidth: 200 },
      {
        headerName: "Email",
        field: "email",
        minWidth: 220,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Phone",
        field: "phone",
        minWidth: 160,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Active",
        field: "isActive",
        minWidth: 90,
        maxWidth: 100,
        valueFormatter: (p) => yesNoFormatter(p.value),
      },
      {
        headerName: "Trusted KYC",
        field: "trustedKycAutoApprove",
        minWidth: 120,
        valueFormatter: (p) => yesNoFormatter(p.value),
      },
      {
        headerName: "KYC Review",
        field: "kycReviewRequired",
        minWidth: 120,
        valueFormatter: (p) => yesNoFormatter(p.value),
      },
      {
        headerName: "Created",
        field: "createdAt",
        minWidth: 180,
        valueFormatter: (p) => formatDisplayDate(p.value),
        sortable: true,
      },
      {
        headerName: "Actions",
        field: "actions",
        minWidth: 230,
        maxWidth: 280,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: any) => {
          const isSelected = selectedOwner?.ownerId === data.ownerId;
          return (
            <div className="flex items-center gap-1.5">
              <TableActionButton
                variant={isSelected ? "primary" : "neutral"}
                icon={isSelected ? "check" : "tune"}
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedOwner(data);
                }}
              >
                {isSelected ? "Selected" : "Manage"}
              </TableActionButton>
              <TableActionButton
                variant="neutral"
                icon="mail"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedOwner(data);
                  setUpdateContactOpen(true);
                }}
              >
                Contact
              </TableActionButton>
            </div>
          );
        },
      },
    ],
    [selectedOwner?.ownerId],
  );

  const apiKeysColumnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Key ID",
        field: "ownerApiKeyId",
        minWidth: 90,
        maxWidth: 110,
      },
      { headerName: "Label", field: "label", flex: 1, minWidth: 180 },
      {
        headerName: "Active",
        field: "isActive",
        minWidth: 90,
        maxWidth: 100,
        valueFormatter: (p) => yesNoFormatter(p.value),
      },
      { headerName: "Created By", field: "createdBy", minWidth: 120 },
      {
        headerName: "Created At",
        field: "createdAt",
        minWidth: 180,
        valueFormatter: (p) => formatDisplayDate(p.value),
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        minWidth: 180,
        valueFormatter: (p) => formatDisplayDate(p.value),
      },
      {
        headerName: "Action",
        field: "action",
        minWidth: 120,
        maxWidth: 150,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: any) => {
          const isRevoking = revokingKeyId === data.ownerApiKeyId;
          return (
            <TableActionButton
              variant="danger"
              icon="key_off"
              loading={isRevoking}
              disabled={!toOwnersBoolean(data.isActive)}
              onClick={(event) => {
                event.stopPropagation();
                handleRevokeKey(data.ownerApiKeyId);
              }}
            >
              Revoke
            </TableActionButton>
          );
        },
      },
    ],
    [handleRevokeKey, revokingKeyId],
  );

  const whitelistColumnDefs: ColDef[] = useMemo(
    () => [
      { headerName: "ID", field: "id", minWidth: 70, maxWidth: 90 },
      { headerName: "Address", field: "address", flex: 1, minWidth: 180 },
      {
        headerName: "Partner Merchant",
        field: "partnerMerchantId",
        minWidth: 140,
        valueFormatter: (p) =>
          p.value === null || p.value === undefined ? "—" : p.value,
      },
      {
        headerName: "Label",
        field: "label",
        minWidth: 160,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Active",
        field: "active",
        minWidth: 90,
        maxWidth: 100,
        valueFormatter: (p) => yesNoFormatter(p.value),
      },
      { headerName: "Created By", field: "createdBy", minWidth: 120 },
      {
        headerName: "Created At",
        field: "createdAt",
        minWidth: 180,
        valueFormatter: (p) => formatDisplayDate(p.value),
      },
      {
        headerName: "Actions",
        field: "actions",
        minWidth: 220,
        maxWidth: 270,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: any) => {
          const isToggling = togglingWhitelistId === data.id;
          const isDeleting = deletingWhitelistId === data.id;
          const isActive = toOwnersBoolean(data.active);
          return (
            <div className="flex items-center gap-1.5">
              <TableActionButton
                variant={isActive ? "neutral" : "success"}
                icon={isActive ? "block" : "check_circle"}
                loading={isToggling}
                disabled={isDeleting}
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleWhitelist(data.id, !isActive);
                }}
              >
                {isActive ? "Disable" : "Enable"}
              </TableActionButton>
              <TableActionButton
                variant="danger"
                icon="delete"
                loading={isDeleting}
                disabled={isToggling}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDeleteWhitelist(data.id);
                }}
              >
                Delete
              </TableActionButton>
            </div>
          );
        },
      },
    ],
    [
      deletingWhitelistId,
      handleDeleteWhitelist,
      handleToggleWhitelist,
      togglingWhitelistId,
    ],
  );

  const selectedOwnerLabel = selectedOwner
    ? `${selectedOwner.displayName} (${selectedOwner.code}) — ${selectedOwner.ownerId}`
    : "No owner selected";

  return (
    <>
      <div className="h-[70px] border-b border-border-subtle bg-bg-page-body flex items-center justify-between px-[20px] sticky top-0 z-10 gap-4">
        <div className="flex flex-col">
          <p className="text-text-base text-[14px] font-semibold">
            Owner Management
          </p>
          <p className="text-text-subtle text-[11px]">{selectedOwnerLabel}</p>
        </div>

        <div className="flex items-center gap-3 flex-1 justify-end">
          <GlobalTextInputField
            type="text"
            value={ownersSearch}
            onChange={(e) => setOwnersSearch(e.target.value)}
            placeholder="Search ownerId / code / name / email / phone"
            prefixIcon={
              <span className="material-symbols-outlined text-text-subtle">
                search
              </span>
            }
          />
          <LightCustomButton
            text="CREATE OWNER"
            onClick={() => setCreateOwnerOpen(true)}
            isActive={true}
            className="min-w-[120px] shrink-0"
          />
        </div>
      </div>

      <div
        className="bg-bg-page-body p-[16px] flex flex-col gap-4"
        style={{ height: "calc(100dvh - 70px)" }}
      >
        <div className="flex flex-col gap-2 h-[44%] min-h-[260px]">
          <p className="text-text-base text-[12px] font-semibold">Owners</p>
          {ownersLoading ? (
            <AgGridSkeleton
              columnDefs={ownersColumnDefs}
              style={{ height: "100%", width: "100%" }}
            />
          ) : (
            <div className="ag-theme-alpine ag-grid-12px flex-1 min-h-0">
              <AgGridReactTable
                ref={ownersGridRef}
                rowData={ownersFiltered}
                columnDefs={ownersColumnDefs}
                error={ownersError}
                getRowId={(p) => p.data.ownerId.toString()}
                onRowClicked={(p: any) => setSelectedOwner(p.data)}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
          <div className="flex flex-col gap-2 min-h-0">
            <div className="flex items-center justify-between">
              <p className="text-text-base text-[12px] font-semibold">
                Owner API Keys
              </p>
              <button
                className="text-[11px] text-text-subtle hover:text-text-base"
                onClick={() =>
                  selectedOwner?.ownerId && fetchApiKeys(selectedOwner.ownerId)
                }
                disabled={!selectedOwner?.ownerId}
              >
                Refresh
              </button>
            </div>

            {!selectedOwner?.ownerId ? (
              <div className="flex-1 rounded border border-border-subtle bg-bg-page-body flex items-center justify-center text-text-subtle text-[12px]">
                Select an owner to view API keys
              </div>
            ) : apiKeysLoading ? (
              <AgGridSkeleton
                columnDefs={apiKeysColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="ag-theme-alpine ag-grid-12px flex-1 min-h-0">
                <AgGridReactTable
                  ref={apiKeysGridRef}
                  rowData={apiKeys}
                  columnDefs={apiKeysColumnDefs}
                  error={apiKeysError}
                  getRowId={(p) => p.data.ownerApiKeyId.toString()}
                />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 min-h-0">
            <div className="flex items-center justify-between">
              <p className="text-text-base text-[12px] font-semibold">
                IP Whitelist
              </p>
              <div className="flex items-center gap-2">
                <button
                  className="text-[11px] text-text-subtle hover:text-text-base"
                  onClick={() =>
                    selectedOwner?.ownerId &&
                    fetchWhitelist(selectedOwner.ownerId)
                  }
                  disabled={!selectedOwner?.ownerId}
                >
                  Refresh
                </button>
                <LightCustomButton
                  text="ADD IP"
                  onClick={() => {
                    if (!selectedOwner?.ownerId) {
                      handleError({ message: "Select an owner first" });
                      return;
                    }
                    setAddWhitelistOpen(true);
                  }}
                  isActive={!!selectedOwner?.ownerId}
                  className="min-w-[90px]"
                />
              </div>
            </div>

            {!selectedOwner?.ownerId ? (
              <div className="flex-1 rounded border border-border-subtle bg-bg-page-body flex items-center justify-center text-text-subtle text-[12px]">
                Select an owner to manage IP whitelist
              </div>
            ) : whitelistLoading ? (
              <AgGridSkeleton
                columnDefs={whitelistColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <div className="ag-theme-alpine ag-grid-12px flex-1 min-h-0">
                <AgGridReactTable
                  ref={whitelistGridRef}
                  rowData={whitelistEntries}
                  columnDefs={whitelistColumnDefs}
                  error={whitelistError}
                  getRowId={(p) => p.data.id.toString()}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateOwnerSlider
        isOpen={createOwnerOpen}
        onClose={() => setCreateOwnerOpen(false)}
        onSuccess={fetchOwners}
      />
      <UpdateOwnerContactSlider
        isOpen={updateContactOpen}
        owner={selectedOwner}
        onClose={() => setUpdateContactOpen(false)}
        onSuccess={fetchOwners}
      />
      <AddOwnerIpWhitelistSlider
        isOpen={addWhitelistOpen}
        ownerId={selectedOwner?.ownerId ?? null}
        onClose={() => setAddWhitelistOpen(false)}
        onSuccess={refreshSelectedOwnerData}
      />
    </>
  );
};

export default OwnersManagement;
